import React, { useState, useEffect } from "react";
import "./Gallery.css";
import { Link } from "react-router-dom";
import Footer from "../Footer/Footer";
import NavBar from "../NavBar/NavBar";
import { GalleryPhoto } from "../../Data";
import { Helmet } from "react-helmet-async";

const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filteredImages, setFilteredImages] = useState(GalleryPhoto);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(12);
  const [isMobile, setIsMobile] = useState(false);

  // Extract unique categories
  const categories = ["all", ...new Set(GalleryPhoto.map(img => img.category).filter(Boolean))];

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",   // Change to "smooth" if you prefer
    });
  }, []);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    setTimeout(() => setIsLoading(false), 500);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (selectedCategory === "all") {
      setFilteredImages(GalleryPhoto);
    } else {
      setFilteredImages(GalleryPhoto.filter(img => img.category === selectedCategory));
    }
    setVisibleCount(12);
  }, [selectedCategory]);

  const openLightbox = (image, index) => {
    setCurrentImage(image);
    setCurrentIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCurrentImage(null);
    document.body.style.overflow = "auto";
  };

  const nextImage = () => {
    const nextIndex = (currentIndex + 1) % filteredImages.length;
    setCurrentIndex(nextIndex);
    setCurrentImage(filteredImages[nextIndex]);
  };

  const prevImage = () => {
    const prevIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    setCurrentIndex(prevIndex);
    setCurrentImage(filteredImages[prevIndex]);
  };

  const loadMore = () => {
    setVisibleCount(prev => prev + 12);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, currentIndex]);

  const visibleImages = filteredImages.slice(0, visibleCount);
  const hasMore = visibleCount < filteredImages.length;

  return (
    <>
    <Helmet>
     <title>Gallery</title>
     <meta
      name="description"
      content="Capturing moments of excellence, dedication, and joy 
        "/>
     <link rel="canonical" href="/"/>
     </Helmet>
    <NavBar />

      {/* Hero Section */}
      <section 
        className="gallery-hero"
        style={{
          background: `linear-gradient(135deg, rgba(0,0,0,0.75), rgba(0,0,0,0.6)), url('/images/moving2.jpg')`,
          backgroundAttachment: 'fixed',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="gallery-hero-content">
          <div className="hero-text">
            <span className="hero-subtitle">Our Memories</span>
            <h1>Kigali International <span>Gymnastics Academy</span></h1>
            <p>Capturing moments of excellence, dedication, and joy</p>
            <div className="hero-breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span className="current">Gallery</span>
            </div>
          </div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" />
          </svg>
        </div>
      </section>

      {/* Stats Section */}
      <section className="gallery-stats">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <i className="fa-solid fa-camera"></i>
              <span className="stat-number">{GalleryPhoto.length}+</span>
              <span className="stat-label">Moments Captured</span>
            </div>
            <div className="stat-card">
              <i className="fa-solid fa-medal"></i>
              <span className="stat-number">50+</span>
              <span className="stat-label">Events</span>
            </div>
            <div className="stat-card">
              <i className="fa-solid fa-users"></i>
              <span className="stat-number">200+</span>
              <span className="stat-label">Happy Students</span>
            </div>
            <div className="stat-card">
              <i className="fa-solid fa-trophy"></i>
              <span className="stat-number">15+</span>
              <span className="stat-label">Competitions</span>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Our Gallery</span>
            <h2>Moments That Matter</h2>
            <p>Explore our journey through gymnastics excellence</p>
          </div>

          {/* Category Filters */}
          <div className="gallery-filters">
            {categories.map((category) => (
              <button
                key={category}
                className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category === 'all' ? 'All Photos' : category}
                {category !== 'all' && (
                  <span className="count">
                    {GalleryPhoto.filter(img => img.category === category).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading beautiful moments...</p>
            </div>
          ) : (
            <>
              {/* Mosaic Grid */}
              <div className="mosaic-grid">
                {visibleImages.map((image, index) => (
                  <div 
                    key={index} 
                    className={`mosaic-item mosaic-item-${(index % 6) + 1}`}
                    onClick={() => openLightbox(image, index)}
                  >
                    <div className="mosaic-inner">
                      <img 
                        src={image.image} 
                        alt={image.title || `Gallery ${index + 1}`}
                        loading="lazy"
                      />
                      <div className="mosaic-overlay">
                        <div className="overlay-content">
                          <i className="fa-solid fa-magnifying-glass-plus"></i>
                          <h4>{image.title || 'Gymnastics Moment'}</h4>
                          {image.category && <span>{image.category}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="load-more">
                  <button onClick={loadMore} className="load-more-btn">
                    <i className="fa-solid fa-images"></i>
                    Load More Photos
                  </button>
                </div>
              )}

              {/* No Results */}
              {filteredImages.length === 0 && (
                <div className="no-results">
                  <i className="fa-solid fa-image"></i>
                  <h3>No photos found</h3>
                  <p>Try a different category</p>
                  <button onClick={() => setSelectedCategory('all')} className="btn-primary">
                    View All Photos
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Video Section */}
      <section className="video-section">
        <div className="container">
          <div className="video-content">
            <div className="video-text">
              <span className="section-tag">Watch Us in Action</span>
              <h2>Experience the Energy</h2>
              <p>See our students showcasing their skills and passion for gymnastics</p>
            </div>
            <div className="video-wrapper">
              <div className="video-placeholder">
                <i className="fa-solid fa-play"></i>
                <p>Gymnastics Highlights Reel</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gallery-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Join Our Journey?</h2>
            <p>Be part of our growing gymnastics family</p>
            <div className="cta-buttons">
              <Link to="/classes" className="btn-primary">Find Classes</Link>
              <Link to="/contact" className="btn-secondary">Contact Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && currentImage && (
        <div className="lightbox" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <i className="fa-solid fa-times"></i>
            </button>
            <button className="lightbox-prev" onClick={prevImage}>
              <i className="fa-solid fa-chevron-left"></i>
            </button>
            <div className="lightbox-image">
              <img src={currentImage.image} alt="Gallery preview" />
            </div>
            <button className="lightbox-next" onClick={nextImage}>
              <i className="fa-solid fa-chevron-right"></i>
            </button>
            <div className="lightbox-caption">
              <h4>{currentImage.title || 'Gymnastics Moment'}</h4>
              <p>{currentImage.category || 'KIGA Gallery'}</p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default Gallery;